import React from 'react';
import { getElementDefinition } from './elementRegistry';
import Canvas from './Canvas';

// Import layout element renderers
import SectionElement from './elements/layout/SectionElement';
import RowElement from './elements/layout/RowElement';
import ColumnElement from './elements/layout/ColumnElement';
import GridElement from './elements/layout/GridElement';
import GridContainerElement from './elements/layout/GridContainerElement';
import GridCellElement from './elements/layout/GridCellElement';

// Import content element renderers
import HeadingElement from './elements/content/HeadingElement';
import ParagraphElement from './elements/content/ParagraphElement';
import RichTextElement from './elements/content/RichTextElement';
import DividerElement from './elements/content/DividerElement';
import BulletListElement from './elements/content/BulletListElement';
import OrderedListElement from './elements/content/OrderedListElement';

// Import media element renderers (placeholders for now)
import ImageElement from './elements/media/ImageElement';
import GalleryElement from './elements/media/GalleryElement';
import VideoElement from './elements/media/VideoElement';

// Import form element renderers (placeholders for now)
import TextInputElement from './elements/form/TextInputElement';
import TextAreaElement from './elements/form/TextAreaElement';
import SelectElement from './elements/form/SelectElement';
import CheckboxElement from './elements/form/CheckboxElement';
import RadioElement from './elements/form/RadioElement';
import SignatureElement from './elements/form/SignatureElement';

// Import action element renderers (placeholders for now)
import ButtonElement from './elements/action/ButtonElement';
import CTAElement from './elements/action/CTAElement';

// Import dynamic element renderers (placeholders for now)
import StaffDirectoryElement from './elements/dynamic/StaffDirectoryElement';
import ProgramShowcaseElement from './elements/dynamic/ProgramShowcaseElement';

/**
 * ElementRenderer - Routes elements to their appropriate renderer components
 *
 * Handles both container elements (with children) and content elements
 * Recursively renders nested structures
 */
const ElementRenderer = ({
  element,
  path,
  onElementSelect,
  selectedElementId,
  isEditorMode,
  onElementDelete
}) => {
  const definition = getElementDefinition(element.type);

  if (!definition) {
    return (
      <div className="unknown-element" style={{ padding: '1rem', border: '1px dashed red' }}>
        <strong>Unknown Element Type:</strong> {element.type}
      </div>
    );
  }

  // Get the appropriate renderer component
  const RendererComponent = getRendererComponent(element.type);

  if (!RendererComponent) {
    return (
      <div className="missing-renderer" style={{ padding: '1rem', border: '1px dashed orange' }}>
        <strong>No Renderer:</strong> {element.type}
      </div>
    );
  }

  // Special case: grid-container renders children directly (no Canvas/Droppable wrapper)
  // Grid cells already have their own individual drop zones
  // Grid cells are NOT draggable - they're auto-positioned by CSS Grid
  if (element.type === 'grid-container' && element.children) {
    return (
      <RendererComponent
        element={element}
        path={path}
        isEditorMode={isEditorMode}
      >
        {element.children.map((child, index) => {
          const childPath = [...path, child.id];
          const isSelected = child.id === selectedElementId;

          return (
            <div
              key={child.id}
              className={`grid-cell-wrapper ${isSelected ? 'selected' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                if (onElementSelect) {
                  onElementSelect(child.id, childPath);
                }
              }}
            >
              <ElementRenderer
                element={child}
                path={childPath}
                onElementSelect={onElementSelect}
                selectedElementId={selectedElementId}
                isEditorMode={isEditorMode}
                onElementDelete={onElementDelete}
              />
            </div>
          );
        })}
      </RendererComponent>
    );
  }

  // If this is a container element, pass Canvas for rendering children
  if (definition.isContainer && element.children) {
    return (
      <RendererComponent
        element={element}
        path={path}
        isEditorMode={isEditorMode}
      >
        <Canvas
          elements={element.children}
          path={path}
          onElementSelect={onElementSelect}
          selectedElementId={selectedElementId}
          isEditorMode={isEditorMode}
          onElementDelete={onElementDelete}
          parentType={element.type}
        />
      </RendererComponent>
    );
  }

  // Regular content element
  return (
    <RendererComponent
      element={element}
      path={path}
      isEditorMode={isEditorMode}
    />
  );
};

/**
 * Map element types to their renderer components
 */
const getRendererComponent = (type) => {
  const renderers = {
    // Layout
    'section': SectionElement,
    'row': RowElement,
    'column': ColumnElement,
    'grid': GridElement,
    'grid-container': GridContainerElement,
    'grid-cell': GridCellElement,

    // Content
    'heading': HeadingElement,
    'paragraph': ParagraphElement,
    'richtext': RichTextElement,
    'divider': DividerElement,
    'bullet-list': BulletListElement,
    'ordered-list': OrderedListElement,

    // Media
    'image': ImageElement,
    'gallery': GalleryElement,
    'video': VideoElement,

    // Form
    'text-input': TextInputElement,
    'textarea': TextAreaElement,
    'select': SelectElement,
    'checkbox': CheckboxElement,
    'radio': RadioElement,
    'signature': SignatureElement,

    // Action
    'button': ButtonElement,
    'cta': CTAElement,

    // Dynamic
    'staff-directory': StaffDirectoryElement,
    'program-showcase': ProgramShowcaseElement
  };

  return renderers[type] || null;
};

export default ElementRenderer;
